/** @format */

import {
  CalendarClock,
  Clock3,
  Eye,
  Menu,
  Mail,
  Plus,

  Users,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useEffect, useMemo, useState } from "react";
import WeeklyWindowPicker from "../../components/demo/WeeklyWindowPicker";
import Header from "../../components/ui/Header";
import Modal from "../../components/ui/Modal";
import Toast from "../../components/ui/Toast";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import {
  DemoInviteRecord,
  demoInviteService,
} from "../../api/demoInviteService";
import { organizationService } from "../../api/organizationService";
import OrgFeatureFlagsEditor from "../../components/org/OrgFeatureFlagsEditor";
import { useToast } from "../../hooks/useToast";
import { useOrganizationStore } from "../../store/useOrganizationStore";

type WindowRow = { start_at: string; end_at: string };
type FormErrors = {
  email?: string;
  orgName?: string;
  maxSeats?: string;
  maxSops?: string;
  maxUploadBytes?: string;
  inviteExpiresAt?: string;
  windows: { start_at?: string; end_at?: string }[];
};

const MAX_UPLOAD_OPTIONS = [
  { label: "25 MB", value: 25 * 1024 * 1024 },
  { label: "50 MB", value: 50 * 1024 * 1024 },
  { label: "100 MB", value: 100 * 1024 * 1024 },
  { label: "250 MB", value: 250 * 1024 * 1024 },
  { label: "500 MB", value: 500 * 1024 * 1024 },
  { label: "1 GB", value: 1024 * 1024 * 1024 },
];

interface DemoInvitesAdminProps {
  embedded?: boolean;
}

export default function DemoInvitesAdmin({
  embedded = false,
}: DemoInvitesAdminProps) {
  const { toast, showToast, hideToast } = useToast();
  const organizations = useOrganizationStore((s) => s.organizations);
  const fetchOrganizations = useOrganizationStore((s) => s.fetchOrganizations);
  const [rows, setRows] = useState<DemoInviteRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [maxSeats, setMaxSeats] = useState<number>(2);
  const [maxSops, setMaxSops] = useState<number>(5);
  const [maxUploadBytes, setMaxUploadBytes] = useState<number>(104857600);
  const [inviteExpiresAt, setInviteExpiresAt] = useState("");
  const [windows, setWindows] = useState<WindowRow[]>([]);
  const [features, setFeatures] = useState<Record<string, boolean>>({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [detailsInviteId, setDetailsInviteId] = useState<string | null>(null);
  const [detailsWindows, setDetailsWindows] = useState<WindowRow[]>([]);
  const [initialDetailsWindows, setInitialDetailsWindows] = useState<WindowRow[]>([]);
  const [isSavingDetailsWindows, setIsSavingDetailsWindows] = useState(false);
  const [detailsMaxSeats, setDetailsMaxSeats] = useState<number>(1);
  const [detailsMaxSops, setDetailsMaxSops] = useState<number>(1);
  const [detailsMaxUploadBytes, setDetailsMaxUploadBytes] = useState<number>(
    MAX_UPLOAD_OPTIONS[0].value,
  );
  const [initialDetailsLimits, setInitialDetailsLimits] = useState({
    maxSeats: 1,
    maxSops: 1,
    maxUploadBytes: MAX_UPLOAD_OPTIONS[0].value,
  });
  const [isSavingDetailsLimits, setIsSavingDetailsLimits] = useState(false);
  const [detailsMembersUsed, setDetailsMembersUsed] = useState<number | null>(
    null,
  );
  const [isLoadingDetailsMembersUsed, setIsLoadingDetailsMembersUsed] =
    useState(false);
  const [showUnsavedDetailsDialog, setShowUnsavedDetailsDialog] =
    useState(false);
  const [showUnsavedCreateDialog, setShowUnsavedCreateDialog] =
    useState(false);
  const [detailsFeaturesDirty, setDetailsFeaturesDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await demoInviteService.list();
      setRows(list);
    } finally {
      setLoading(false);
    }
  };

  const openDetails = async (inviteId: string) => {
    await fetchOrganizations();
    setDetailsInviteId(inviteId);
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEmail("");
    setOrgName("");
    setMaxSeats(2);
    setMaxSops(5);
    setMaxUploadBytes(104857600);
    setInviteExpiresAt("");
    setWindows([]);
    setFeatures({});
    setSubmitAttempted(false);
  };

  const filteredRows = useMemo(() => {
    if (statusFilter === "all") return rows;
    return rows.filter((row) => row.status === statusFilter);
  }, [rows, statusFilter]);

  const organizationsById = useMemo(() => {
    return new Map(organizations.map((org) => [org.orgId, org]));
  }, [organizations]);

  const detailsInvite = useMemo(
    () => rows.find((invite) => invite.id === detailsInviteId) ?? null,
    [rows, detailsInviteId],
  );
  const detailsOrg = detailsInvite
    ? organizationsById.get(detailsInvite.orgId)
    : undefined;
  const detailsEnabledFeatures = Object.entries(
    detailsOrg?.features ?? {},
  ).filter(([, enabled]) => Boolean(enabled));

  const stats = useMemo(() => {
    const active = rows.filter((row) => row.status !== "revoked").length;
    const accepted = rows.filter((row) => row.acceptedAt).length;
    return { total: rows.length, active, accepted };
  }, [rows]);

  const nowTimestamp = Date.now();

  const validation = useMemo(() => {
    const errors: FormErrors = { windows: windows.map(() => ({})) };
    const schemaErrors: string[] = [];

    const trimmedEmail = email.trim();
    const trimmedOrgName = orgName.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!trimmedEmail) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(trimmedEmail)) {
      errors.email = "Enter a valid email address.";
    }

    if (!trimmedOrgName) {
      errors.orgName = "Organization name is required.";
    }

    if (!Number.isInteger(maxSeats) || maxSeats < 1) {
      errors.maxSeats =
        "Max seats must be an integer greater than or equal to 1.";
    }

    if (!Number.isInteger(maxSops) || maxSops < 1) {
      errors.maxSops =
        "Max SOPs must be an integer greater than or equal to 1.";
    }

    if (!MAX_UPLOAD_OPTIONS.some((option) => option.value === maxUploadBytes)) {
      errors.maxUploadBytes = "Select a valid upload size.";
    }

    if (inviteExpiresAt) {
      const expirationTimestamp = new Date(inviteExpiresAt).getTime();
      if (Number.isNaN(expirationTimestamp)) {
        errors.inviteExpiresAt = "Invite expiration date is invalid.";
      } else if (expirationTimestamp <= nowTimestamp) {
        errors.inviteExpiresAt = "Invite expiration must be in the future.";
      }
    }

    windows.forEach((windowRow, index) => {
      const hasStart = Boolean(windowRow.start_at);
      const hasEnd = Boolean(windowRow.end_at);

      if (!hasStart && !hasEnd) return;

      if (!hasStart) {
        errors.windows[index].start_at =
          "Start date is required when end date is set.";
        return;
      }

      if (!hasEnd) {
        errors.windows[index].end_at =
          "End date is required when start date is set.";
        return;
      }

      const startTimestamp = new Date(windowRow.start_at).getTime();
      const endTimestamp = new Date(windowRow.end_at).getTime();

      if (Number.isNaN(startTimestamp)) {
        errors.windows[index].start_at = "Start date is invalid.";
      }

      if (Number.isNaN(endTimestamp)) {
        errors.windows[index].end_at = "End date is invalid.";
      }

      if (
        !Number.isNaN(startTimestamp) &&
        !Number.isNaN(endTimestamp) &&
        endTimestamp <= startTimestamp
      ) {
        errors.windows[index].end_at =
          "End date must be later than start date.";
      }
    });

    if (
      Object.values(errors).some((value) => {
        if (Array.isArray(value))
          return value.some(
            (windowError) => windowError.start_at || windowError.end_at,
          );
        return Boolean(value);
      })
    ) {
      schemaErrors.push(
        "Please fix the highlighted fields before creating the invite.",
      );
    }

    return {
      errors,
      schemaErrors,
      hasErrors: schemaErrors.length > 0,
    };
  }, [
    email,
    inviteExpiresAt,
    maxSeats,
    maxSops,
    maxUploadBytes,
    nowTimestamp,
    orgName,
    windows,
  ]);

  const extractApiErrorMessage = (err: unknown, fallback: string): string => {
    const axiosErr = err as {
      response?: { data?: { message?: string; error?: string } };
    };
    return (
      axiosErr?.response?.data?.message ??
      axiosErr?.response?.data?.error ??
      (err instanceof Error ? err.message : fallback)
    );
  };

  const toIsoFromLocalInput = (value: string) => {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  };

  useEffect(() => {
    if (!detailsInvite) {
      setDetailsWindows([]);
      setDetailsMembersUsed(null);
      return;
    }
    const nextWindows =
      detailsInvite.windows?.map((w) => ({
        start_at: w.startAt ?? "",
        end_at: w.endAt ?? "",
      })) ?? [];
    setDetailsWindows(nextWindows);
    setInitialDetailsWindows(nextWindows);
    setDetailsMaxSeats(detailsOrg?.maxSeats ?? 1);
    setDetailsMaxSops(detailsOrg?.maxSops ?? 1);
    setDetailsMaxUploadBytes(
      detailsOrg?.maxUploadBytes ?? MAX_UPLOAD_OPTIONS[0].value,
    );
    setInitialDetailsLimits({
      maxSeats: detailsOrg?.maxSeats ?? 1,
      maxSops: detailsOrg?.maxSops ?? 1,
      maxUploadBytes: detailsOrg?.maxUploadBytes ?? MAX_UPLOAD_OPTIONS[0].value,
    });
  }, [
    detailsInvite,
    detailsOrg?.maxSeats,
    detailsOrg?.maxSops,
    detailsOrg?.maxUploadBytes,
  ]);

  useEffect(() => {
    if (!detailsInvite?.orgId) return;
    let cancelled = false;
    setIsLoadingDetailsMembersUsed(true);

    organizationService
      .listMembers(detailsInvite.orgId)
      .then((members) => {
        if (cancelled) return;
        setDetailsMembersUsed(members.length);
      })
      .catch(() => {
        if (cancelled) return;
        setDetailsMembersUsed(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingDetailsMembersUsed(false);
      });

    return () => {
      cancelled = true;
    };
  }, [detailsInvite?.orgId]);


  const saveDetailsWindows = async () => {
    if (!detailsInvite) return;
    setIsSavingDetailsWindows(true);
    try {
      const payload = detailsWindows.filter((w) => w.start_at && w.end_at);
      await demoInviteService.replaceWindows(detailsInvite.id, payload);
      setInitialDetailsWindows(detailsWindows);
      await load();
      showToast(
        "Access windows saved",
        "Demo access windows were updated.",
        "success",
      );
    } catch (err) {
      const message = extractApiErrorMessage(
        err,
        "Failed to update access windows.",
      );
      showToast("Failed to save windows", message, "error");
    } finally {
      setIsSavingDetailsWindows(false);
    }
  };

  const saveDetailsLimits = async () => {
    if (!detailsInvite) return;
    if (!Number.isInteger(detailsMaxSeats) || detailsMaxSeats < 1) {
      showToast("Invalid max seats", "Max seats must be at least 1.", "error");
      return;
    }
    if (!Number.isInteger(detailsMaxSops) || detailsMaxSops < 1) {
      showToast("Invalid max SOPs", "Max SOPs must be at least 1.", "error");
      return;
    }
    if (
      !MAX_UPLOAD_OPTIONS.some(
        (option) => option.value === detailsMaxUploadBytes,
      )
    ) {
      showToast(
        "Invalid max upload size",
        "Select a valid upload size.",
        "error",
      );
      return;
    }

    setIsSavingDetailsLimits(true);
    try {
      await organizationService.updateOrganization(detailsInvite.orgId, {
        maxSeats: detailsMaxSeats,
        maxSops: detailsMaxSops,
        maxUploadBytes: detailsMaxUploadBytes,
      });
      setInitialDetailsLimits({
        maxSeats: detailsMaxSeats,
        maxSops: detailsMaxSops,
        maxUploadBytes: detailsMaxUploadBytes,
      });
      await fetchOrganizations();
      showToast("Limits saved", "Demo limits were updated.", "success");
    } catch (err) {
      const message = extractApiErrorMessage(
        err,
        "Failed to update demo limits.",
      );
      showToast("Failed to save limits", message, "error");
    } finally {
      setIsSavingDetailsLimits(false);
    }
  };

  const formatDateTime = (value?: string | null) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString();
  };

  const hasUnsavedCreateChanges = useMemo(
    () =>
      email.trim() !== "" ||
      orgName.trim() !== "" ||
      inviteExpiresAt !== "" ||
      windows.length > 0 ||
      Object.keys(features).length > 0,
    [email, orgName, inviteExpiresAt, windows, features],
  );

  const closeCreateModal = () => {
    if (hasUnsavedCreateChanges) {
      setShowUnsavedCreateDialog(true);
      return;
    }
    setCreateOpen(false);
    resetForm();
  };

  const discardAndCloseCreateModal = () => {
    setShowUnsavedCreateDialog(false);
    setCreateOpen(false);
    resetForm();
  };

  const hasUnsavedDetailsChanges = useMemo(() => {
    const windowsDirty =
      JSON.stringify(detailsWindows) !== JSON.stringify(initialDetailsWindows);
    const limitsDirty =
      detailsMaxSeats !== initialDetailsLimits.maxSeats ||
      detailsMaxSops !== initialDetailsLimits.maxSops ||
      detailsMaxUploadBytes !== initialDetailsLimits.maxUploadBytes;
    return windowsDirty || limitsDirty || detailsFeaturesDirty;
  }, [
    detailsWindows,
    initialDetailsWindows,
    detailsMaxSeats,
    detailsMaxSops,
    detailsMaxUploadBytes,
    detailsFeaturesDirty,
    initialDetailsLimits.maxSeats,
    initialDetailsLimits.maxSops,
    initialDetailsLimits.maxUploadBytes,
  ]);

  const closeDetailsModal = () => {
    if (hasUnsavedDetailsChanges) {
      setShowUnsavedDetailsDialog(true);
      return;
    }
    setDetailsInviteId(null);
  };

  const discardAndCloseDetailsModal = () => {
    setShowUnsavedDetailsDialog(false);
    setDetailsFeaturesDirty(false);
    setDetailsInviteId(null);
  };

  const create = async () => {
    setSubmitAttempted(true);
    if (validation.hasErrors) {
      showToast(
        "Validation error",
        validation.schemaErrors[0] ?? "Please review the highlighted fields.",
        "error",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await demoInviteService.create({
        email: email.trim(),
        org_name: orgName.trim(),
        max_seats: maxSeats,
        max_sops: maxSops,
        max_upload_bytes: maxUploadBytes,
        invite_expires_at: inviteExpiresAt
          ? toIsoFromLocalInput(inviteExpiresAt) ?? undefined
          : undefined,
        windows: windows.filter((w) => w.start_at && w.end_at),
        features,
      });
      setCreateOpen(false);
      resetForm();
      await load();
      showToast(
        "Invite created",
        "Demo invite was created successfully.",
        "success",
      );
    } catch (err) {
      const message = extractApiErrorMessage(
        err,
        "Failed to create demo invite.",
      );
      showToast("Failed to create invite", message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <main
      className={embedded ? "px-2 pb-6 sm:px-0" : "pt-24 px-4 pb-10 sm:px-6"}
    >
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="grid gap-3 md:grid-cols-4">
          <div className="rounded-lg border border-slate-700/40 bg-slate-900/50 p-4">
            <p className="text-xs text-slate-400">Total invites</p>
            <p className="mt-1 text-2xl font-semibold">{stats.total}</p>
          </div>
          <div className="rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
            <p className="text-xs text-blue-200/80">Active</p>
            <p className="mt-1 text-2xl font-semibold text-blue-100">
              {stats.active}
            </p>
          </div>
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-4">
            <p className="text-xs text-emerald-200/80">Accepted</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-100">
              {stats.accepted}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-slate-300">Invite history</h2>

          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-slate-600 bg-slate-900 px-3 py-2 text-sm text-slate-200"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
            </select>
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-md border border-blue-500/40 bg-blue-500/15 px-3 py-2 text-sm text-blue-200 hover:bg-blue-500/25"
            >
              <Plus className="h-4 w-4" />
              New invite
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-700/40 bg-slate-900/40">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-10 text-slate-400">
              <Clock3 className="h-5 w-5 animate-pulse" />
              Loading invites...
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 p-12 text-center">
              <Mail className="h-10 w-10 text-slate-600" />
              <p className="text-slate-300">
                No invites found for this filter.
              </p>
              <p className="text-sm text-slate-500">
                Create a new invite to start demo org onboarding.
              </p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-900/80">
                <tr>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Org</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  return (
                    <tr
                      key={r.id}
                      className="border-t border-slate-700/30 hover:bg-slate-800/30"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-slate-500" />
                          <span>{r.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">{r.orgName || r.orgId}</td>
                      <td className="px-4 py-3">
                        <span
                          title={
                            r.acceptedAt
                              ? `Accepted at ${formatDateTime(r.acceptedAt)}`
                              : "Not accepted yet"
                          }
                          className={`rounded-full px-2 py-1 text-xs font-medium ${
                            r.status === "accepted"
                              ? "bg-emerald-500/20 text-emerald-200"
                              : "bg-blue-500/20 text-blue-200"
                          }`}
                        >
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {formatDateTime(r.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <DropdownMenu.Root>
                          <DropdownMenu.Trigger asChild>
                            <button
                              type="button"
                              className="rounded-lg border border-slate-600/50 bg-slate-700/30 p-2 text-slate-300 transition-colors hover:bg-slate-600/40 hover:text-white"
                              aria-label="Invite actions"
                            >
                              <Menu className="h-4 w-4" />
                            </button>
                          </DropdownMenu.Trigger>
                          <DropdownMenu.Portal>
                            <DropdownMenu.Content
                              className="z-50 min-w-[180px] rounded-lg border border-slate-600/50 bg-slate-800 py-1 shadow-xl"
                              align="end"
                              sideOffset={4}
                            >
                              <DropdownMenu.Item
                                className="flex cursor-pointer select-none items-center gap-2 rounded px-3 py-2 text-sm text-slate-200 outline-none hover:bg-slate-700/80 hover:text-white focus:bg-slate-700/80 data-[highlighted]:bg-slate-700/80"
                                onSelect={() => {
                                  void openDetails(r.id);
                                }}
                              >
                                <Eye className="h-4 w-4 shrink-0 text-slate-400" />
                                View details
                              </DropdownMenu.Item>
                            </DropdownMenu.Content>
                          </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </main>
  );

  return (
    <div
      className={
        embedded
          ? "text-white"
          : "min-h-screen bg-gradient-to-br from-black via-gray-950 to-black text-white"
      }
    >
      {!embedded && <Header />}
      {content}

      <Modal
        open={createOpen}
        onClose={closeCreateModal}
        title="Create invite"
        size="large"
      >
        <div className="space-y-4 pr-1">
          <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-300" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                  1. Basic information
                </p>
                <p className="text-xs text-slate-500">
                  Core invite and organization details.
                </p>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="space-y-1">
                <span className="text-xs text-slate-400">
                  Invite email <span className="text-rose-300">*</span>
                </span>
                <input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  type="email"
                  className={`w-full rounded border bg-slate-900 px-3 py-2 ${
                    submitAttempted && validation.errors.email
                      ? "border-rose-500"
                      : "border-slate-600"
                  }`}
                />
                {submitAttempted && validation.errors.email && (
                  <p className="text-xs text-rose-300">{validation.errors.email}</p>
                )}
              </label>
              <label className="space-y-1">
                <span className="text-xs text-slate-400">
                  Organization name <span className="text-rose-300">*</span>
                </span>
                <input
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="Acme Inc."
                  className={`w-full rounded border bg-slate-900 px-3 py-2 ${
                    submitAttempted && validation.errors.orgName
                      ? "border-rose-500"
                      : "border-slate-600"
                  }`}
                />
                {submitAttempted && validation.errors.orgName && (
                  <p className="text-xs text-rose-300">{validation.errors.orgName}</p>
                )}
              </label>
              <label className="space-y-1 sm:col-span-2">
                <span className="text-xs text-slate-400">
                  Invite expiration <span className="text-slate-500">(optional)</span>
                </span>
                <input
                  type="datetime-local"
                  value={inviteExpiresAt}
                  onChange={(e) => setInviteExpiresAt(e.target.value)}
                  className={`w-full rounded border bg-slate-900 px-3 py-2 ${
                    submitAttempted && validation.errors.inviteExpiresAt
                      ? "border-rose-500"
                      : "border-slate-600"
                  }`}
                />
                {submitAttempted && validation.errors.inviteExpiresAt && (
                  <p className="text-xs text-rose-300">
                    {validation.errors.inviteExpiresAt}
                  </p>
                )}
              </label>
            </div>
          </section>

          <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Users className="h-4 w-4 text-blue-300" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                  2. Demo limits
                </p>
                <p className="text-xs text-slate-500">
                  Capacity, SOP, and upload controls.
                </p>
              </div>
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <label className="space-y-1">
                <span className="text-xs text-slate-400">Max seats</span>
                <input
                  type="number"
                  min={1}
                  value={maxSeats}
                  onChange={(e) => setMaxSeats(Number(e.target.value))}
                  className={`w-full rounded border bg-slate-900 px-3 py-2 text-sm ${
                    submitAttempted && validation.errors.maxSeats
                      ? "border-rose-500"
                      : "border-slate-600"
                  }`}
                />
                {submitAttempted && validation.errors.maxSeats && (
                  <p className="text-xs text-rose-300">{validation.errors.maxSeats}</p>
                )}
              </label>
              <label className="space-y-1">
                <span className="text-xs text-slate-400">Max SOPs</span>
                <input
                  type="number"
                  min={1}
                  value={maxSops}
                  onChange={(e) => setMaxSops(Number(e.target.value))}
                  className={`w-full rounded border bg-slate-900 px-3 py-2 text-sm ${
                    submitAttempted && validation.errors.maxSops
                      ? "border-rose-500"
                      : "border-slate-600"
                  }`}
                />
                {submitAttempted && validation.errors.maxSops && (
                  <p className="text-xs text-rose-300">{validation.errors.maxSops}</p>
                )}
              </label>
              <label className="space-y-1">
                <span className="text-xs text-slate-400">Max upload size</span>
                <select
                  value={maxUploadBytes}
                  onChange={(e) => setMaxUploadBytes(Number(e.target.value))}
                  className={`w-full rounded border bg-slate-900 px-3 py-2 text-sm ${
                    submitAttempted && validation.errors.maxUploadBytes
                      ? "border-rose-500"
                      : "border-slate-600"
                  }`}
                >
                  {MAX_UPLOAD_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {submitAttempted && validation.errors.maxUploadBytes && (
                  <p className="text-xs text-rose-300">
                    {validation.errors.maxUploadBytes}
                  </p>
                )}
              </label>
            </div>
          </section>

          <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
            <div className="mb-3 flex items-center gap-2">
              <CalendarClock className="h-4 w-4 text-blue-300" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                  3. Access windows
                </p>
                <p className="text-xs text-slate-500">
                  Drag on the calendar to create windows. Drag edges to resize.
                </p>
              </div>
            </div>
            <WeeklyWindowPicker windows={windows} onChange={setWindows} />
          </section>

          <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
            <div className="mb-3 flex items-center gap-2">
              <Eye className="h-4 w-4 text-blue-300" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                  4. Resources
                </p>
                <p className="text-xs text-slate-500">
                  Feature flags enabled for this demo tenant.
                </p>
              </div>
            </div>
            <OrgFeatureFlagsEditor initialFeatures={features} onSaved={(f) => setFeatures(f)} />
          </section>

          <div className="flex justify-end gap-2 border-t border-slate-700/50 pt-4">
            <button
              onClick={closeCreateModal}
              className="rounded border border-slate-600 px-3 py-2 text-slate-200 hover:bg-slate-800"
            >
              Cancel
            </button>
            <button
              onClick={create}
              disabled={isSubmitting}
              className="rounded border border-blue-500/30 bg-blue-500/20 px-3 py-2 text-blue-100 disabled:opacity-60"
            >
              {isSubmitting ? "Creating..." : "Create"}
            </button>
          </div>
        </div>
      </Modal>
      <Modal
        open={detailsInvite !== null}
        onClose={closeDetailsModal}
        title="Demo details"
        size="large"
      >
        {detailsInvite && (
          <div className="space-y-4 pr-2">
            <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Mail className="h-4 w-4 text-blue-300" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                    1. Basic information
                  </p>
                  <p className="text-xs text-slate-500">
                    Core invite and organization details.
                  </p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-700/70 bg-slate-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    Invite email
                  </p>
                  <p className="mt-1 break-all text-sm text-slate-200">
                    {detailsInvite.email}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-700/70 bg-slate-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    Organization
                  </p>
                  <p className="mt-1 text-sm text-slate-200">
                    {detailsInvite.orgName || detailsInvite.orgId}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-700/70 bg-slate-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    Status
                  </p>
                  <p className="mt-1 text-sm text-slate-200">
                    {detailsInvite.status}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-700/70 bg-slate-900 p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    Invite expiration
                  </p>
                  <p className="mt-1 text-sm text-slate-200">
                    {formatDateTime(detailsInvite.inviteExpiresAt)}
                  </p>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-300" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                    2. Demo limits
                  </p>
                  <p className="text-xs text-slate-500">
                    Capacity, SOP, and upload controls.
                  </p>
                </div>
              </div>
              <div className="grid gap-3 md:grid-cols-3">
                <label className="space-y-1">
                  <span className="text-xs text-slate-400">Max seats</span>
                  <input
                    type="number"
                    min={1}
                    value={detailsMaxSeats}
                    onChange={(e) => setDetailsMaxSeats(Number(e.target.value))}
                    className="w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-400">Max SOPs</span>
                  <input
                    type="number"
                    min={1}
                    value={detailsMaxSops}
                    onChange={(e) => setDetailsMaxSops(Number(e.target.value))}
                    className="w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm"
                  />
                </label>
                <label className="space-y-1">
                  <span className="text-xs text-slate-400">Max upload size</span>
                  <select
                    value={detailsMaxUploadBytes}
                    onChange={(e) => setDetailsMaxUploadBytes(Number(e.target.value))}
                    className="w-full rounded border border-slate-600 bg-slate-900 px-3 py-2 text-sm"
                  >
                    {MAX_UPLOAD_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="mt-3 rounded-md border border-slate-700/50 bg-slate-900/60 p-2.5 text-xs text-slate-300">
                {isLoadingDetailsMembersUsed
                  ? "Checking seat usage..."
                  : `Seats currently used: ${detailsMembersUsed ?? "-"}`}
              </div>
              {detailsMembersUsed !== null && detailsMaxSeats < detailsMembersUsed && (
                <div className="mt-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200">
                  New seat limit is below current usage. You would need to remove at
                  least{" "}
                  <span className="font-semibold">
                    {detailsMembersUsed - detailsMaxSeats}
                  </span>{" "}
                  member(s).
                </div>
              )}
              <div className="mt-3">
                <button
                  type="button"
                  onClick={saveDetailsLimits}
                  disabled={isSavingDetailsLimits}
                  className="rounded border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-xs text-blue-100 disabled:opacity-60"
                >
                  {isSavingDetailsLimits ? "Saving..." : "Save limits"}
                </button>
              </div>
            </section>

            <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
              <div className="mb-3 flex items-center gap-2">
                <CalendarClock className="h-4 w-4 text-blue-300" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                    3. Access windows
                  </p>
                  <p className="text-xs text-slate-500">
                    Drag on the calendar to create windows. Drag edges to resize.
                  </p>
                </div>
              </div>
              <WeeklyWindowPicker windows={detailsWindows} onChange={setDetailsWindows} />
              <div className="mt-3 flex justify-start">
                <button
                  type="button"
                  onClick={saveDetailsWindows}
                  disabled={isSavingDetailsWindows}
                  className="rounded border border-blue-500/30 bg-blue-500/20 px-3 py-1.5 text-xs text-blue-100 disabled:opacity-60"
                >
                  {isSavingDetailsWindows ? "Saving..." : "Save windows"}
                </button>
              </div>
            </section>

            <section className="rounded-xl border border-slate-700/60 bg-slate-900/45 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Eye className="h-4 w-4 text-blue-300" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                    4. Resources
                  </p>
                  <p className="text-xs text-slate-500">
                    Feature flags enabled for this demo tenant.
                  </p>
                </div>
              </div>
              {detailsEnabledFeatures.length === 0 && (
                <p className="mb-2 text-sm text-slate-500">
                  No feature flags currently enabled.
                </p>
              )}
              {detailsInvite.orgId && (
                <OrgFeatureFlagsEditor
                  orgId={detailsInvite.orgId}
                  initialFeatures={detailsOrg?.features ?? {}}
                  onDirtyChange={setDetailsFeaturesDirty}
                  onSaved={async () => {
                    setDetailsFeaturesDirty(false);
                    await fetchOrganizations();
                    await load();
                    showToast(
                      "Features saved",
                      "Feature flags were updated.",
                      "success",
                    );
                  }}
                />
              )}
            </section>
          </div>
        )}
      </Modal>
      <Toast
        open={toast.open}
        onOpenChange={hideToast}
        title={toast.title ?? ""}
        description={toast.description}
        type={toast.type}
        duration={toast.duration}
      />
      <ConfirmDialog
        open={showUnsavedCreateDialog}
        onClose={() => setShowUnsavedCreateDialog(false)}
        onConfirm={discardAndCloseCreateModal}
        title="Unsaved changes"
        description="You have unsaved changes in this form. Leave anyway and discard them?"
        confirmLabel="Discard changes"
        cancelLabel="Continue editing"
        variant="danger"
      />
      <ConfirmDialog
        open={showUnsavedDetailsDialog}
        onClose={() => setShowUnsavedDetailsDialog(false)}
        onConfirm={discardAndCloseDetailsModal}
        title="Unsaved changes"
        description="You have unsaved changes in this form. Leave anyway and discard them?"
        confirmLabel="Discard changes"
        cancelLabel="Continue editing"
        variant="danger"
      />
    </div>
  );
}
